/**
 * @file PhotoTextColorField.tsx
 * @description «Текст на фото» фона секции (phase19.16, партия SB120,
 * этап 1; uiux-sb120-stage1 §2.3; решение 2026-10-18 (в) из
 * db-design-sb120-stage1: `textTone` остаётся, `textColor?` — поверх).
 *
 * `ColorField mode="text"`: ячейки-наборы «Тёмный набор» / «Светлый набор»
 * (`textTone` dark / light — заголовок, текст и ссылка разом; выбор набора
 * снимает `textColor`), роли темы и «Свой» (`textColor` — один цвет всего
 * текста секции; у элементов со своим цветом он не перекрывает их цвет).
 * Под фото контраст не считается: подсказка «проверьте на канве» — одна,
 * справочное предупреждение `text_on_photo` под полем (LookPhotoOptions),
 * поэтому своей строки контраста у поля нет (`elementBg` не передаётся).
 */

import { DEFAULT_SITE_THEME, SITE_TEXT_COLOR_ROLES, type SectionMediaBg } from "@chaiberi/shared";

import { ColorField, type ColorTextValue } from "../../../site-fields/ColorField";
import { useSectionForm } from "../section-form-context";
import { TEXT_COLOR_ROLE_LABELS as ROLE_LABELS } from "../../../../lib/site-fields/text-color-labels";

interface Props {
  bg: SectionMediaBg;
  onChange: (bg: SectionMediaBg) => void;
}

export function PhotoTextColorField({ bg, onChange }: Props) {
  const colors = (useSectionForm().theme ?? DEFAULT_SITE_THEME).colors;
  const value: ColorTextValue<(typeof SITE_TEXT_COLOR_ROLES)[number]> = bg.textColor ?? { kind: "auto" };
  const tone = (textTone: SectionMediaBg["textTone"]) => {
    const next = { ...bg, textTone };
    delete next.textColor;
    onChange(next);
  };
  return (
    <ColorField
      mode="text"
      label="Текст на фото"
      value={value}
      roles={SITE_TEXT_COLOR_ROLES}
      labels={ROLE_LABELS}
      colors={colors}
      presets={[
        { id: "dark", label: "Тёмный набор", color: colors.text, active: bg.textTone === "dark", onSelect: () => tone("dark") },
        { id: "light", label: "Светлый набор", color: colors.onDark, active: bg.textTone === "light", onSelect: () => tone("light") },
      ]}
      onChange={(v) => {
        if (v.kind === "auto") return;
        onChange({ ...bg, textColor: v });
      }}
    />
  );
}
